import type { GeoJson } from '@ais-ui/api-spec/shapes';
import type { RefObject } from 'react';
import { ReportShapeItem, ShapeType } from '../../reportsTypes';
import * as z from 'zod';
import { geometrySchema } from '@ais-ui/utils';

function findShapeById(shapes: GeoJson[], id: string): GeoJson | undefined {
  for (const shape of shapes) {
    if (shape.properties?.id === id) return shape;
    if (shape.type === 'FeatureCollection' && shape.features) {
      const found = findShapeById(shape.features, id);
      if (found) return found;
    }
  }
  return undefined;
}

function allFeaturesArePolygons(shape: GeoJson): boolean {
  if (!shape.features) return false;
  return shape.features.every((feature) => {
    if (feature.type === 'FeatureCollection') {
      return allFeaturesArePolygons(feature);
    }
    return feature.geometry?.type === 'Polygon';
  });
}

export function createDynamicShapeZodSchema(
  shapeItems: ReportShapeItem[] | undefined,
  type: ShapeType,
  instantShape?: boolean | undefined,
  shapesRef?: RefObject<GeoJson[] | undefined>,
) {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  if (!shapeItems) {
    return z.object(schemaFields);
  }

  if (type === 'multiShape') {
    for (const shapeItem of shapeItems) {
      const minRequired = shapeItem.minItems ?? 1;
      const message = `${shapeItem.label ?? 'Shape'} requires at least ${minRequired} item${minRequired > 1 ? 's' : ''}`;
      // The string represents an id of a shape and the geometrySchema an ad hoc created shape
      let fieldSchema: z.ZodTypeAny = z
        .array(z.union([z.string().min(1, message), geometrySchema]))
        .min(minRequired, message);

      if (shapeItem.allowFeatureCollections && shapesRef) {
        fieldSchema = fieldSchema.pipe(
          z
            .array(z.union([z.string(), geometrySchema]))
            .superRefine((val, ctx) => {
              const shapes = shapesRef.current;
              if (!shapes) return;
              for (const item of val) {
                if (typeof item !== 'string') continue;
                const shape = findShapeById(shapes, item);
                if (
                  shape?.type === 'FeatureCollection' &&
                  !allFeaturesArePolygons(shape)
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Feature collection must contain only polygons',
                  });
                }
              }
            }),
        );
      }

      schemaFields[shapeItem.name] = fieldSchema;
    }
  } else if (type === 'worldShape') {
    for (const shapeItem of shapeItems) {
      schemaFields[shapeItem.name] = z.boolean();
    }
  } else if (type === 'ports') {
    for (const shapeItem of shapeItems) {
      const message = `${shapeItem.label ?? 'Shape'} is required`;
      schemaFields[shapeItem.name] = z
        .array(z.string().min(1, message))
        .min(1, message);
    }
  } else if (instantShape) {
    // For instant shapes: Accept Geometry OR empty string (before drawing)
    for (const shapeItem of shapeItems) {
      schemaFields[shapeItem.name] = z.union([
        geometrySchema,
        z.string().length(0), // Allow empty string before drawing
        z.null(),
      ]);
    }
  } else {
    for (const shapeItem of shapeItems) {
      const message = `${shapeItem.label ?? 'Shape'} is required`;
      let fieldSchema: z.ZodTypeAny = z
        .string({ error: message })
        .min(1, message);

      if (shapeItem.allowFeatureCollections && shapesRef) {
        fieldSchema = fieldSchema.pipe(
          z.string().superRefine((val, ctx) => {
            const shapes = shapesRef.current;
            if (!shapes || !val) return;
            const shape = findShapeById(shapes, val);
            if (
              shape?.type === 'FeatureCollection' &&
              !allFeaturesArePolygons(shape)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Feature collection must contain only polygons',
              });
            }
          }),
        );
      }

      schemaFields[shapeItem.name] = fieldSchema;
    }
  }

  return z.object(schemaFields);
}
